// react
import { useMemo } from 'react';
import styled from 'styled-components';

// components
import { ArticleCard, Button, Title } from '@/components/atoms';
import { CardContainer } from '@/components/templates';

// store
import { Article } from '@karehero/models';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import theme from '@/theme';

/**
 * SuggestedArticles displays a list of suggested articles.
 */
export const SuggestedArticles = ({
  articles,
  bookmarkedArticles,
  onBookmark,
  isLoading = true,
}: SuggestedArticlesProps) => {
  // hooks
  const [width] = useWindowSize();

  // memos
  const maxColumns = useMemo(() => {
    if (width > theme.breakpoint.xlValue) return 4;
    if (width > theme.breakpoint.mdValue) return 2;
    return 1;
  }, [width]);

  return (
    <Wrapper>
      <Header>
        <Title size='sm' isNoMargin>
          Suggested for you
        </Title>
        <Button ariaLabel='See all' to='/library' variant='secondary' size='sm'>
          See all
        </Button>
      </Header>
      <CardContainer maxColumns={maxColumns}>
        {(articles || [{}, {}, {}, {}]).map((article, i) => (
          <ArticleCard
            key={i}
            article={article}
            isBookmarked={
              !!bookmarkedArticles?.find((a) => a._id === article._id)
            }
            onBookmark={onBookmark}
            isLoading={isLoading}
          />
        ))}
      </CardContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing[20]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export interface SuggestedArticlesProps {
  articles?: Partial<Article>[];
  bookmarkedArticles?: Article[];
  onBookmark?: (article: Partial<Article>) => void;
  isLoading?: boolean;
}

SuggestedArticles.displayName = 'SuggestedArticles';

export default SuggestedArticles;
